import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminReferralApi, adminReferralApiKeys } from '@/api';
import { AccountsList, AccountsTree, SetAgentModal, SetAssistantModal } from '@/components';
import { Box } from '@/components/common/atoms';
import { Col, Row, Typography } from 'antd';
import { Container, Flex } from '@/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserAddOutlined } from '@ant-design/icons';
import { COLORS } from '@/utils/static';
import { usePopups } from '@/hooks';
import { queryClient } from '@/app/QueryClientProvider';
import { useSelector } from 'react-redux';

export const InvestorReferralRequests = () => {
  const { id } = useParams();
  const investor = useSelector((state) => state.investor);
  const { showErrorToast } = usePopups();
  const { t } = useTranslation();
  const [addAssistantOpened, setAddAssistantOpened] = useState(false);
  const [addAgentOpened, setAddAgentOpened] = useState(false);

  const referralsTree = useQuery({
    queryKey: [adminReferralApiKeys.getReferralsTree, id],
    queryFn: () =>
      adminReferralApi.getReferralsTree({
        investorId: +id
      }),
    select: (data1) => data1.data,
    onError: () => showErrorToast('Structure fetch error')
  });

  const agents = useQuery({
    queryKey: [adminReferralApiKeys.getAgentsList, id],
    queryFn: () =>
      adminReferralApi.getAgentsList({
        investorId: +id
      }),
    select: (data1) => data1.data,
    onError: () => showErrorToast('Agent fetch error')
  });

  const recommenders = useQuery({
    queryKey: [adminReferralApiKeys.getRecommendersList, id],
    queryFn: () =>
      adminReferralApi.getRecommendersList({
        investorId: +id
      }),
    select: (data1) => data1.data,
    onError: () => showErrorToast('Assistant fetch error')
  });

  const { mutateAsync: deleteAgent } = useMutation({
    mutationFn: adminReferralApi.deleteReferralCodeAgent,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries([adminReferralApiKeys.getReferralsTree, id]),
        queryClient.invalidateQueries([adminReferralApiKeys.getAgentsList, id])
      ])
  });
  const { mutateAsync: deleteRecommender } = useMutation({
    mutationFn: adminReferralApi.deleteReferralCodeRecommender,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries([adminReferralApiKeys.getReferralsTree, id]),
        queryClient.invalidateQueries([adminReferralApiKeys.getRecommendersList, id])
      ])
  });

  return (
    <Container $paddingY={25} $marginBottom={40}>
      <Flex align={'center'} justify={'space-between'} wrap>
        <Typography.Title level={2}>{t('Referral requests')}</Typography.Title>
      </Flex>

      <Box $marginTop={36}>
        <Row wrap={false} gutter={24}>
          {(investor.accountRole === 'RECOMMENDER' || investor.accountRole === 'AGENT') && (
            <Col span={8}>
              <Typography.Title level={4} style={{ marginBottom: '5px' }}>
                {t('Structure')}
              </Typography.Title>
              <AccountsTree
                accounts={{
                  list: referralsTree.data,
                  error: referralsTree.isError,
                  isLoading: referralsTree.isLoading
                }}
                emptyTitle={t('No structure yet')}
                onDeleteRecommender={deleteRecommender}
                onDeleteAgent={deleteAgent}
                adminView
              />
            </Col>
          )}

          <Col span={8}>
            <Typography.Title level={4} style={{ marginBottom: '5px' }}>
              {t('Assistant')} <AddButton onClick={() => setAddAssistantOpened(true)} />
            </Typography.Title>
            <AccountsList
              accounts={{
                list: recommenders.data,
                error: recommenders.isError,
                isLoading: recommenders.isLoading
              }}
              emptyTitle={t('No assistant yet')}
              onDelete={deleteRecommender}
              role='RECOMMENDERS'
              structureView
            />
          </Col>

          <Col span={8}>
            <Typography.Title level={4} style={{ marginBottom: '5px' }}>
              {t('Agent')} <AddButton onClick={() => setAddAgentOpened(true)} />
            </Typography.Title>
            <AccountsList
              accounts={{ list: agents.data, error: agents.isError, isLoading: agents.isLoading }}
              emptyTitle={t('No agent yet')}
              onDelete={deleteAgent}
              role='AGENTS'
              structureView
            />
          </Col>
        </Row>
      </Box>

      <SetAssistantModal
        open={addAssistantOpened}
        onClose={() => setAddAssistantOpened(false)}
        isFirst={recommenders.data && recommenders.data.length === 0}
      />

      <SetAgentModal
        open={addAgentOpened}
        onClose={() => setAddAgentOpened(false)}
        isFirst={agents.data && agents.data.length === 0}
      />
    </Container>
  );
};

const AddButton = styled(UserAddOutlined)`
  width: 32px;
  height: 32px;
  color: ${COLORS.aragonBlue};
  cursor: pointer;
  margin-left: 8px;
  border-radius: 6px;
  transition: 0.2s;
  :hover {
    box-shadow: 0px 2px 10px 2px rgba(8, 190, 229, 0.15) inset;
  }
  :active {
    box-shadow: 0px 2px 10px 2px rgba(8, 190, 229, 0.32) inset;
  }

  > svg {
    width: 100%;
  }
`;
